// Utilidad para generar y descargar un archivo KML a partir de posiciones históricas
// Uso: generateAndDownloadKML(positions, { unitName, from, to, parkedPositions, lang })

import { formatDateToLocalString, generateHourMinuteLabel } from './Utils';
import { appIntl } from './context/IntlGlobalProvider';

type Lang = 'es' | 'en';

interface HistoricalPositionLike {
  latitude: number;
  longitude: number;
  gpsDate?: string;
  locationName?: string;
  speed?: number;
  eventName?: string;
  eventId?: number | null;
  endDate?: string;
  eventColor?: string; // RRGGBB sin #
  eventTextColor?: string; // RRGGBB sin #
  eventPrimaryColor?: string; // RRGGBB sin #
}

interface KMLMeta {
  unitName?: string;
  from?: string; // ISO
  to?: string; // ISO
  parkedPositions?: HistoricalPositionLike[];
  lang?: Lang;
}

// ──────────────────────────────────────────
// i18n
// ──────────────────────────────────────────

const LABELS: Record<Lang, Record<string, string>> = {
  es: {
    date: 'Fecha',
    speed: 'Velocidad',
    location: 'Ubicación',
    duration: 'Duración',
    event: 'Evento',
    kmh: 'km/h',
    positions: 'Posiciones',
    trajectory: 'Trayectoria',
    historic: 'Histórico',
    exportDesc: 'Exportación de posiciones históricas',
  },
  en: {
    date: 'Date',
    speed: 'Speed',
    location: 'Location',
    duration: 'Duration',
    event: 'Event',
    kmh: 'km/h',
    positions: 'Positions',
    trajectory: 'Trajectory',
    historic: 'Historic',
    exportDesc: 'Historical positions export',
  },
};

function getEventName(
  eventId: number | null | undefined,
  fallback?: string
): string | undefined {
  if (eventId == null) return fallback;
  try {
    const name = appIntl().formatMessage({ id: `organizations.event${eventId}` });
    return name !== `organizations.event${eventId}` ? name : fallback;
  } catch {
    return fallback;
  }
}

// ──────────────────────────────────────────
// Color helpers
// ──────────────────────────────────────────

const DEFAULT_EVENT_COLOR = '0055FF';
const DEFAULT_PARKED_COLOR = 'FD7E14'; // naranja parking

// Convierte RRGGBB (con o sin #) al formato KML AABBGGRR
function hexToKmlColor(hex: string, alpha = 'ff'): string {
  const clean = (hex || DEFAULT_EVENT_COLOR).replace('#', '').padEnd(6, '0');
  const r = clean.slice(0, 2);
  const g = clean.slice(2, 4);
  const b = clean.slice(4, 6);
  return `${alpha}${b}${g}${r}`;
}

// ──────────────────────────────────────────
// Formato fechas / duración
// ──────────────────────────────────────────

const DATE_FORMAT: Record<Lang, string> = {
  es: 'DD/MM/YYYY HH:mm:ss',
  en: 'MM/DD/YYYY HH:mm:ss',
};

function durationLabel(start: string, end: string): string {
  const seconds = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
  if (seconds <= 0) return '';
  return generateHourMinuteLabel(seconds);
}

// ──────────────────────────────────────────
// XML helpers
// ──────────────────────────────────────────

const escapeXml = (value: any) =>
  (value ?? '')
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

function buildDescriptionHtml(
  color: string,
  textColor: string,
  title: string,
  rows: [string, string | undefined][]
): string {
  const validRows = rows.filter(([, v]) => v !== undefined && v !== null && v !== '');
  const tableRows = validRows
    .map(
      ([k, v]) =>
        `<tr><td style="font-weight:bold;padding:3px 8px 3px 0;white-space:nowrap;color:#555">${k}</td><td style="padding:3px 0">${v}</td></tr>`
    )
    .join('');
  const header = `<div style="background:#${color};color:#${textColor};padding:5px 8px;font-weight:bold;font-size:13px;border-radius:4px 4px 0 0;margin-bottom:4px">${title}</div>`;
  const table = `<table style="font-size:12px;border-collapse:collapse;width:100%">${tableRows}</table>`;
  return `<div style="font-family:Arial,sans-serif;min-width:200px">${header}${table}</div>`;
}

// ──────────────────────────────────────────
// Generación de estilos KML dinámicos
// ──────────────────────────────────────────

const PARKED_ICON = 'http://maps.google.com/mapfiles/kml/paddle/P.png';
const EVENT_ICON = 'http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png';

function buildDynamicStyles(entries: { color: string; isParked: boolean }[]): string {
  const seen = new Set<string>();
  const styles: string[] = [
    `<Style id="lineStyle"><LineStyle><color>ff00aa55</color><width>3</width></LineStyle></Style>`,
  ];

  for (const { color, isParked } of entries) {
    const id = styleId(color, isParked);
    if (seen.has(id)) continue;
    seen.add(id);
    const kmlColor = hexToKmlColor(color);
    const icon = isParked ? PARKED_ICON : EVENT_ICON;
    const scale = isParked ? '1.1' : '0.8';
    styles.push(
      `<Style id="${id}"><IconStyle><color>${kmlColor}</color><scale>${scale}</scale><Icon><href>${icon}</href></Icon></IconStyle></Style>`
    );
  }

  return styles.join('\n    ');
}

function styleId(color: string, isParked: boolean): string {
  return `${isParked ? 'pkd' : 'evt'}-${color.replace('#', '').toLowerCase()}`;
}

// ──────────────────────────────────────────
// Placemark
// ──────────────────────────────────────────

function buildPlacemark(
  p: HistoricalPositionLike,
  idx: number,
  isParked: boolean,
  color: string,
  textColor: string,
  lang: Lang
): string {
  const t = LABELS[lang];
  const coords = `${p.longitude},${p.latitude},0`;
  const when = p.gpsDate ? `<TimeStamp><when>${escapeXml(p.gpsDate)}</when></TimeStamp>` : '';
  const dateStr = p.gpsDate
    ? formatDateToLocalString(new Date(p.gpsDate), DATE_FORMAT[lang]) ?? undefined
    : undefined;

  let descHtml: string;
  if (isParked) {
    const duration = p.gpsDate && p.endDate ? durationLabel(p.gpsDate, p.endDate) : undefined;
    const title = getEventName(5) ?? 'Parked';
    descHtml = buildDescriptionHtml(color, textColor, title, [
      [t.date, dateStr],
      [t.location, p.locationName],
      [t.duration, duration],
    ]);
  } else {
    const eventName = getEventName(p.eventId, p.eventName);
    const label = idx + 1;
    const hasEvent = p.eventId != null && p.eventId != 0;
    const speedStr = p.speed != null ? `${p.speed} ${t.kmh}` : undefined;
    const title = hasEvent && eventName ? `${label} – ${eventName}` : String(label);
    descHtml = buildDescriptionHtml(color, textColor, title, [
      [t.date, dateStr],
      [t.speed, speedStr],
      [t.location, p.locationName],
      ...(eventName ? ([[t.event, eventName]] as [string, string | undefined][]) : []),
    ]);
  }

  const placemarkLabel = isParked
    ? `P${idx + 1}`
    : (() => {
        const eventName = getEventName(p.eventId, p.eventName);
        const hasEvent = p.eventId != null && p.eventId != 0;
        return hasEvent && eventName ? `${idx + 1} – ${eventName}` : String(idx + 1);
      })();

  const description = `<description><![CDATA[${descHtml}]]></description>`;

  return `
        <Placemark>
          <name>${escapeXml(placemarkLabel)}</name>
          ${when}
          ${description}
          <styleUrl>#${styleId(color, isParked)}</styleUrl>
          <Point><coordinates>${coords}</coordinates></Point>
        </Placemark>`;
}

// ──────────────────────────────────────────
// Build KML
// ──────────────────────────────────────────

export function buildKML(positions: HistoricalPositionLike[], meta: KMLMeta = {}): string {
  const lang: Lang = meta.lang ?? 'es';
  const t = LABELS[lang];

  const byDate = (a: HistoricalPositionLike, b: HistoricalPositionLike) =>
    new Date(a.gpsDate ?? 0).getTime() - new Date(b.gpsDate ?? 0).getTime();

  const safePositions = (positions || [])
    .filter((p) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
    .sort(byDate);
  const safeParked = (meta.parkedPositions || [])
    .filter((p) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
    .sort(byDate);

  const DEFAULT_TEXT_COLOR = 'ffffff';

  const allSorted = [
    ...safePositions.map((p) => ({
      p,
      isParked: false,
      color: p.eventColor || DEFAULT_EVENT_COLOR,
      textColor: p.eventTextColor || DEFAULT_TEXT_COLOR,
    })),
    ...safeParked.map((p) => ({
      p,
      isParked: true,
      color: p.eventColor || DEFAULT_PARKED_COLOR,
      textColor: p.eventTextColor || DEFAULT_TEXT_COLOR,
    })),
  ].sort((a, b) => new Date(a.p.gpsDate ?? 0).getTime() - new Date(b.p.gpsDate ?? 0).getTime());

  const header = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  const kmlOpen = `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>`;
  const name = `<name>${escapeXml(
    `${t.historic} ${meta.unitName || ''} ${meta.from || ''} ${meta.to || ''}`.trim()
  )}</name>`;
  const docDescription = `<description>${escapeXml(
    `${t.exportDesc}${meta.unitName ? ' - ' + meta.unitName : ''}`
  )}</description>`;

  const styles = buildDynamicStyles(allSorted.map(({ color, isParked }) => ({ color, isParked })));

  const positionsFolder = allSorted.length
    ? `<Folder><name>${t.positions}</name>${allSorted
        .map(({ p, isParked, color, textColor }, i) => buildPlacemark(p, i, isParked, color, textColor, lang))
        .join('\n')}</Folder>`
    : '';

  const lineString = safePositions.length
    ? `
      <Placemark>
        <name>${t.trajectory}</name>
        <styleUrl>#lineStyle</styleUrl>
        <LineString>
          <tessellate>1</tessellate>
          <coordinates>
            ${safePositions.map((p) => `${p.longitude},${p.latitude},0`).join(' ')}
          </coordinates>
        </LineString>
      </Placemark>`
    : '';

  const kmlClose = `</Document></kml>`;
  return [header, kmlOpen, name, docDescription, styles, positionsFolder, lineString, kmlClose]
    .filter(Boolean)
    .join('\n');
}

// ──────────────────────────────────────────
// Download helpers
// ──────────────────────────────────────────

export function downloadKML(kmlString: string, fileName = 'historico.kml') {
  const blob = new Blob([kmlString], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

export function generateAndDownloadKML(
  positions: HistoricalPositionLike[],
  meta: KMLMeta = {},
  fileName?: string
) {
  const kml = buildKML(positions, meta);
  const finalName =
    fileName || `historico_${meta.unitName || 'unidad'}_${meta.from || ''}_${meta.to || ''}.kml`;
  downloadKML(kml, finalName.replace(/\s+/g, '_'));
}

export default generateAndDownloadKML;
